import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Database,
  FileText,
  Gauge,
  KeyRound,
  LayoutDashboard,
  LogOut,
  PanelsTopLeft,
  ScrollText,
  Shield,
  ShieldAlert,
  Table2,
  Terminal,
  Users,
  BarChart3,
  Bot,
  ChevronLeft,
  HardDrive,
  Radio,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { api, type Project } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import {
  isProjectNavActive,
  isProjectNavBranchExpanded,
  listProjectPrimaryNavItems,
  PROJECT_NAV_GROUPS,
  resolveProjectNavLabel,
  SHELL_OVERFLOW_CLASS,
  type ProjectNavLeaf,
} from "@/lib/nav";
import {
  BrandMark,
  ConsoleLogo,
  MobileNavSheet,
  Statusline,
  UserMenu,
} from "@/components/AppShell";
import { ErrorBox, PageLoading, Spinner, WarnBox } from "@/components/ui";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";

const NAV_ICONS: Record<string, LucideIcon> = {
  "": LayoutDashboard,
  "/tables": Table2,
  "/sql": Terminal,
  "/database": Database,
  "/storage": HardDrive,
  "/realtime": Radio,
  "/functions": Workflow,
  "/permissions": Shield,
  "/advisors": ShieldAlert,
  "/advisors/security": ShieldAlert,
  "/auth": Users,
  "/applications": PanelsTopLeft,
  "/usage": Gauge,
  "/observability": Activity,
  "/observability/gateway": BarChart3,
  "/observability/database": Database,
  "/observability/logs": ScrollText,
  "/observability/auth": KeyRound,
  "/observability/mcp": Bot,
  "/observability/alerts": AlertTriangle,
  "/observability/reports": FileText,
};

function navIcon(href: string): LucideIcon {
  return NAV_ICONS[href] ?? LayoutDashboard;
}

function NavLinks({
  base,
  pathname,
  onNavigate,
  compact,
}: {
  base: string;
  pathname: string;
  onNavigate?: () => void;
  /** Hide section descriptions for denser mobile drawers. */
  compact?: boolean;
}) {
  return (
    <nav aria-label="Project navigation" className="navigation-menu">
      {PROJECT_NAV_GROUPS.map((group) => (
        <section
          key={group.label}
          aria-labelledby={`nav-${group.label.toLowerCase()}`}
        >
          <div className="navigation-section-heading">
            <h2
              id={`nav-${group.label.toLowerCase()}`}
              className="nav-section-label"
            >
              {group.label}
            </h2>
            {!compact ? (
              <p className="nav-section-description">{group.description}</p>
            ) : null}
          </div>
          <ul className="navigation-list">
            {group.items.map((item) => {
              const href = `${base}${item.href}`;
              const active = isProjectNavActive(pathname, base, item.href);
              const Icon = navIcon(item.href);
              // Desktop always lists children for discovery; compact mobile
              // expands only when the branch is active to keep the drawer short.
              const showChildren =
                Boolean(item.children?.length) &&
                (!compact ||
                  isProjectNavBranchExpanded(pathname, base, item));
              return (
                <li key={item.href || "root"}>
                  <Link
                    to={href}
                    onClick={onNavigate}
                    className={active ? "nav-link-active" : "nav-link"}
                    aria-current={active ? "page" : undefined}
                    aria-expanded={
                      item.children?.length ? showChildren : undefined
                    }
                    title={item.hint}
                  >
                    <span className="nav-link-icon">
                      <Icon
                        className="h-4 w-4"
                        aria-hidden
                        strokeWidth={1.75}
                      />
                    </span>
                    <span className="min-w-0 flex-1 leading-5">
                      {item.label}
                    </span>
                  </Link>
                  {item.children && showChildren ? (
                    <ul
                      className="navigation-sublist"
                      aria-label={`${item.label} pages`}
                    >
                      {item.children.map((child) => {
                        const childHref = `${base}${child.href}`;
                        const childActive = isProjectNavActive(
                          pathname,
                          base,
                          child.href,
                        );
                        return (
                          <li key={child.href}>
                            <Link
                              to={childHref}
                              onClick={onNavigate}
                              className={
                                childActive
                                  ? "nav-sublink-active"
                                  : "nav-sublink"
                              }
                              aria-current={childActive ? "page" : undefined}
                              title={child.hint}
                            >
                              {child.label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </nav>
  );
}

/** Horizontal primary destinations for phone/tablet (below project sidebar). */
function ProjectNavStrip({
  base,
  pathname,
  items,
}: {
  base: string;
  pathname: string;
  items: ProjectNavLeaf[];
}) {
  return (
    <nav
      className="project-nav-strip lg:hidden"
      aria-label="Quick project navigation"
      data-testid="project-nav-strip"
    >
      <div className="project-nav-strip-track">
        {items.map((item) => {
          const href = `${base}${item.href}`;
          const active = isProjectNavActive(pathname, base, item.href);
          // Observability parent: also highlight when on a nested tool page
          const highlight =
            active ||
            (item.href === "/observability" &&
              pathname.startsWith(`${base}/observability`));
          const Icon = navIcon(item.href);
          return (
            <Link
              key={item.href || "root"}
              to={href}
              className={
                highlight ? "project-nav-chip-active" : "project-nav-chip"
              }
              aria-current={active ? "page" : undefined}
              title={item.hint}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden strokeWidth={1.75} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export function ProjectShell({
  projectId,
  children,
}: {
  projectId: string;
  children?: React.ReactNode;
}) {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState<unknown>(null);

  const base = `/dashboard/projects/${projectId}`;
  const pageLabel = useMemo(
    () => resolveProjectNavLabel(pathname, base),
    [pathname, base],
  );
  const primaryNav = useMemo(() => listProjectPrimaryNavItems(), []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate(
        `/login?continue=${encodeURIComponent(location.pathname + location.search)}`,
        { replace: true },
      );
      return;
    }
    let cancelled = false;
    let retryTimer: number | undefined;
    const loadProject = async () => {
      try {
        const { data } = await api.getProject(projectId);
        if (cancelled) return;
        setProject(data);
        setError(null);
        if (data.status === "provisioning" || data.status === "failed") {
          retryTimer = window.setTimeout(() => void loadProject(), 2000);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e);
      }
    };
    void loadProject();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
  }, [user, loading, projectId, navigate, location.pathname, location.search]);

  if (loading || !user) {
    return <PageLoading label="Loading project…" />;
  }

  const databaseRoute = pathname === `${base}/database`;
  const projectDataPlaneReady = project?.status === "active";

  return (
    <TooltipProvider delayDuration={400}>
      <div className={cn("shell-root", SHELL_OVERFLOW_CLASS)}>
        <aside className="navigation-drawer hidden lg:sticky lg:top-0 lg:flex">
          <div className="navigation-drawer-brand">
            <ConsoleLogo to="/dashboard/projects" />
          </div>
          <Separator />
          <div className="navigation-project-context">
            <Link
              to="/dashboard/projects"
              className="mb-3 flex min-h-10 items-center gap-2 rounded-md px-3 text-sm font-medium text-text-muted transition-[color,background-color,transform] duration-standard ease-standard hover:-translate-y-px hover:bg-surface-container hover:text-secondary"
            >
              <ChevronLeft
                className="h-4 w-4"
                aria-hidden
                strokeWidth={1.75}
              />
              All projects
            </Link>
            <div className="rounded-xl border border-border-standard bg-surface-container-lowest px-3 py-3 shadow-card">
              <div className="eyebrow mb-1">Current project</div>
              <div className="truncate font-sans text-sm font-semibold text-text-primary">
                {project?.name ?? "…"}
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                <span className="status-dot" />
                <span className="truncate font-mono text-[10px] text-text-muted">
                  {project?.schema_name ?? "loading schema"}
                </span>
              </div>
            </div>
          </div>
          <ScrollArea className="navigation-drawer-scroll">
            <NavLinks base={base} pathname={pathname} />
          </ScrollArea>
          <div className="navigation-drawer-footer">
            <div className="flex items-center justify-between gap-2 px-1">
              <span className="text-[11px] font-bold uppercase tracking-label text-text-muted">
                project
              </span>
              {project?.status ? (
                <Badge variant="outline" className="capitalize">
                  {project.status}
                </Badge>
              ) : null}
            </div>
          </div>
        </aside>

        <div className="shell-main">
          <header className="shell-header sticky top-0 z-header border-b">
            <div className="shell-header-bar flex h-header items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="lg:hidden">
                  <MobileNavSheet
                    title="Project navigation"
                    description={project?.name ?? "Loading project"}
                    footer={(close) => (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-2 px-1">
                          <span className="text-[11px] font-bold uppercase tracking-label text-text-muted">
                            project
                          </span>
                          {project?.status ? (
                            <Badge variant="outline" className="capitalize">
                              {project.status}
                            </Badge>
                          ) : null}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => {
                            close();
                            void signOut();
                          }}
                        >
                          <LogOut className="h-4 w-4" strokeWidth={1.75} />
                          Sign out
                        </Button>
                      </div>
                    )}
                  >
                    {(close) => (
                      <>
                        <div className="mb-5">
                          <ConsoleLogo to="/dashboard/projects" />
                        </div>
                        <Link
                          to="/dashboard/projects"
                          onClick={close}
                          className="mb-4 flex min-h-11 items-center gap-2 rounded-md px-3 text-sm font-medium text-text-muted transition-[color,background-color,transform] duration-standard ease-standard hover:-translate-y-px hover:bg-surface-container hover:text-secondary"
                        >
                          <ChevronLeft
                            className="h-4 w-4"
                            aria-hidden
                            strokeWidth={1.75}
                          />
                          All projects
                        </Link>
                        <div className="mb-5 rounded-xl border border-border-standard bg-background-subtle px-3 py-3 shadow-card">
                          <div className="eyebrow mb-1">Current project</div>
                          <div className="truncate text-sm font-semibold text-text-primary">
                            {project?.name ?? "…"}
                          </div>
                          <div className="mt-1 flex items-center gap-1.5">
                            <span className="status-dot" aria-hidden />
                            <span className="truncate font-mono text-[10px] text-text-muted">
                              {project?.schema_name ?? "loading schema"}
                            </span>
                          </div>
                        </div>
                        <NavLinks
                          base={base}
                          pathname={pathname}
                          onNavigate={close}
                          compact
                        />
                      </>
                    )}
                  </MobileNavSheet>
                </div>
                <BrandMark className="lg:hidden" />
                <nav
                  className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-1.5"
                  aria-label="Breadcrumb"
                >
                  <div className="flex min-w-0 items-center gap-1.5 text-sm">
                    <Link
                      to="/dashboard/projects"
                      className="hidden text-text-muted transition-colors duration-fast ease-standard hover:text-secondary hover:underline sm:inline"
                    >
                      Projects
                    </Link>
                    <span
                      className="hidden text-text-disabled sm:inline"
                      aria-hidden
                    >
                      /
                    </span>
                    <span className="truncate font-sans font-semibold text-text-primary">
                      {project?.name ?? "…"}
                    </span>
                  </div>
                  <div className="flex min-w-0 items-center gap-1.5 text-xs text-text-muted lg:hidden">
                    <span className="hidden text-text-disabled sm:inline" aria-hidden>
                      /
                    </span>
                    <span className="truncate font-medium text-secondary">
                      {pageLabel}
                    </span>
                  </div>
                </nav>
              </div>
              <UserMenu email={user.email} onSignOut={signOut} />
            </div>

            <Statusline
              mobileMaxItems={2}
              items={[
                {
                  live: true,
                  label: "schema",
                  value: project?.schema_name ?? "…",
                },
                {
                  label: "status",
                  value: (
                    <span className="capitalize">
                      {project?.status ?? "unknown"}
                    </span>
                  ),
                },
                ...(project?.slug
                  ? [{ label: "slug", value: project.slug }]
                  : []),
                { label: "id", value: projectId.slice(0, 8) },
              ]}
            />

            <ProjectNavStrip
              base={base}
              pathname={pathname}
              items={primaryNav}
            />
          </header>

          <main className="shell-content">
            {error ? (
              <div className="mb-4">
                <ErrorBox error={error} />
              </div>
            ) : null}
            {!project ? (
              <Spinner label="Loading project…" />
            ) : !projectDataPlaneReady && !databaseRoute ? (
              <WarnBox>
                Project database is <strong>{project.status}</strong>. Data, Auth, SQL, and Storage become available only after provisioning and validation finish.{" "}
                <Link className="font-semibold underline" to={`${base}/database`}>Open database status</Link>
              </WarnBox>
            ) : children ?? <Outlet />}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}

/** Layout route: reads :id and wraps nested project pages. */
export function ProjectShellRoute() {
  const { id } = useParams<{ id: string }>();
  if (!id) return null;
  return <ProjectShell projectId={id} />;
}
